import { useMemo, type ReactNode } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { type AnswerMap, type FormDefinition, type FormItem } from "@njbsoft/form-logic";
import { FormRenderer, FormLetterhead, parseFormDefinition } from "@njbsoft/form-renderer";
import { renderFacilityRefSlot } from "@/components/form-renderer/facility-ref-slot";
import { type FormTheme } from "@njbsoft/form-ui";

const THEMES: readonly FormTheme[] = ["precision", "warm", "depth", "compact"];
const STATES = ["empty", "filled", "errors", "readonly", "disabled"] as const;
type FixtureState = (typeof STATES)[number];

interface VisualFixtureSearch {
  theme: FormTheme;
  dark: boolean;
  state: FixtureState;
}

/**
 * Server-resolved read-context values a composer would normally fetch — fixed
 * here so record-pull treatments render deterministically with zero network.
 */
const READ_CONTEXT: Record<string, string> = {
  "device.serial_number": "BF-2213-0041",
  "device.model": "Ames Colt 4000SS",
};

const EMPTY_ANSWERS: AnswerMap = {};

const FILLED_ANSWERS: AnswerMap = {
  "fx-facility": "visual-fixture-facility",
  "fx-serial": "BF-2213-0041",
  "fx-device-type": "rp",
  "fx-line-size": 2,
  "fx-test-date": "2026-03-12",
  "fx-test-started": "2026-03-12T14:30:00.000Z",
  "fx-cv1-psid": 6.1,
  "fx-cv2-psid": 5.4,
  "fx-rv-psid": 2.8,
  "fx-cv1-held": true,
  "fx-initial-result": "pass",
  "fx-gauges-zeroed": true,
  "fx-readings-notes":
    "Static line pressure 62 psi. No. 2 shutoff held tight throughout the differential sweep.",
  "fx-hazards": ["irrigation", "boiler", "other"],
  "fx-hazard-other": "Photo-lab silver recovery unit on premises",
  "fx-degree": "high",
  "fx-gauge-id": "TK-5583",
  "fx-gauge-cal-date": "2025-11-02",
  "fx-repairs": [
    {
      "fx-rep-part": "Check valve 1 rubber kit",
      "fx-rep-action": "replaced",
      "fx-rep-cost": 84,
      "fx-rep-labor-hours": 1.5,
    },
    {
      "fx-rep-part": "Relief valve seat",
      "fx-rep-action": "cleaned",
      "fx-rep-cost": 0,
      "fx-rep-labor-hours": 0.5,
    },
  ],
  "fx-attest": true,
};

const ERROR_ANSWERS: AnswerMap = {
  // Answered AND errored on purpose: `fx-serial` is read-bound, so its control
  // carries the record surface and the invalid surface at once (see
  // ERROR_MESSAGES), and a selected `fx-initial-result` pill has to keep its
  // regulatory colour while its group reads as errored.
  "fx-serial": "BF-2213-0041",
  "fx-device-type": "rp",
  "fx-line-size": 30,
  "fx-cv1-psid": 3.2,
  "fx-initial-result": "fail",
  "fx-hazards": ["other"],
  "fx-repairs": [],
};

/**
 * Every entry is a control shape whose errored rendering the baselines must
 * pin. Three of them exist for a specific reason:
 *
 *   - `fx-serial` is record-bound, so its control stacks the base, record, and
 *     invalid surface classes — three arbitrary-value utilities carrying the
 *     same CSS properties, where the winner is decided by generated-CSS order
 *     rather than className order. Nothing but a rendered pixel proves which
 *     one won, so the combined state is photographed rather than reasoned
 *     about.
 *   - `fx-initial-result` (pass/fail pills) and `fx-gauges-zeroed` (switch)
 *     are the two GROUP controls. Their error treatment frames the group
 *     instead of recolouring a control border, and a selected pass/fail pill
 *     must keep its own outcome colour — painting a green PASS pill red would
 *     misreport a regulatory result. `fx-attest` covers the checkbox twin.
 */
const ERROR_MESSAGES: Record<string, string> = {
  "fx-serial": "Serial number does not match the device record.",
  "fx-test-date": "Test date is required.",
  "fx-line-size": "Must be between 0.5 and 12.",
  "fx-initial-result": "Record the initial test result.",
  "fx-gauges-zeroed": "Confirm the gauges were zeroed.",
  "fx-degree": "Select a hazard degree.",
  "fx-hazard-other": "Describe the other hazard.",
  "fx-attest": "You must attest before submitting.",
  "fx-signature": "Tester signature is required.",
};

const ANSWERS_BY_STATE: Record<FixtureState, AnswerMap> = {
  empty: EMPTY_ANSWERS,
  filled: FILLED_ANSWERS,
  errors: ERROR_ANSWERS,
  // readonly + disabled reuse the filled answers: they exercise the same
  // content under the read-only render mode / the locked-control treatment.
  readonly: FILLED_ANSWERS,
  disabled: FILLED_ANSWERS,
};

/** Deterministic stand-in evidence for document / signature items. */
function fixtureEvidence(item: FormItem): ReactNode {
  if (item.type !== "document" && item.type !== "signature") return null;
  return (
    <figure className="mt-2 rounded border border-gray-200 p-2 dark:border-gray-700">
      <div aria-hidden="true" className="h-24 w-40 rounded bg-gray-200 dark:bg-gray-700" />
      <figcaption className="mt-1 text-xs text-muted">
        {item.type === "document" ? "Document (fixture)" : "Tester signature (fixture)"}
      </figcaption>
    </figure>
  );
}

export const Route = createFileRoute("/dev/visual-fixture")({
  validateSearch: (search: Record<string, unknown>): VisualFixtureSearch => ({
    theme: THEMES.includes(search.theme as FormTheme)
      ? // Membership-checked one line above; the cast narrows the unknown
        // search value to the closed FormTheme union.
        (search.theme as FormTheme)
      : "precision",
    dark: search.dark === 1 || search.dark === "1" || search.dark === true,
    state: STATES.includes(search.state as FixtureState)
      ? // Membership-checked one line above, same narrowing as theme.
        (search.state as FixtureState)
      : "empty",
  }),
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  loader: async (): Promise<FormDefinition> => {
    if (!import.meta.env.DEV) throw notFound();
    // Dynamic import inside the DEV-only branch: Vite replaces
    // import.meta.env.DEV with false in production builds and Rollup
    // dead-code-eliminates this import, so the fixture never ships.
    const fixture =
      await import("../../../../../tests/e2e/visual/fixtures/visual-fixture-definition.json");
    // JSON modules widen literal unions (e.g. columns 1|2|3|4 becomes
    // number), so a structural assignment cannot typecheck; the fixture's
    // conformance is enforced by the visual suite rendering it.
    return parseFormDefinition(fixture.default);
  },
  component: VisualFixturePage,
});

function VisualFixturePage() {
  const { theme, dark, state } = Route.useSearch();
  const baseDefinition = Route.useLoaderData();

  const definition = useMemo<FormDefinition>(
    () => ({ ...baseDefinition, presentation: { theme } }),
    [baseDefinition, theme],
  );

  const answers = ANSWERS_BY_STATE[state];
  // Absent onAnswerChange = the renderer's read-only mode (display values,
  // never inputs) — the readonly state exercises the restyled submission view.
  const onAnswerChange = state === "readonly" ? undefined : () => undefined;
  const errorFor = state === "errors" ? (linkId: string) => ERROR_MESSAGES[linkId] : undefined;
  const tagFor =
    state === "empty"
      ? undefined
      : (linkId: string) => (linkId === "fx-serial" ? "From device record" : undefined);
  const attachmentSlot = state === "empty" ? undefined : fixtureEvidence;

  return (
    <div className={dark ? "dark" : undefined}>
      {/* Determinism, then dev-overlay suppression.
       *
       * The router and query devtools mount above this route (the app root and
       * entry respectively) and are themselves DEV-gated, so on a DEV-only route
       * they are ALWAYS present and land in every full-page screenshot: a
       * toggle button, a 1440x500 panel, and a floating launcher. They are not
       * part of the form, and their emotion-generated class names (`go1688…`)
       * change between builds, so a baseline that includes them rots on a
       * dependency bump.
       *
       * Suppressed by the two STABLE hooks rather than those hashes: the router
       * devtools render their toggle and panel inside a `footer`, and the query
       * devtools launcher keeps its `tsqd-` container class. This route renders
       * no footer of its own, so the element selector is unambiguous here. */}
      <style>{`*, *::before, *::after { animation: none !important; transition: none !important; caret-color: transparent !important; scroll-behavior: auto !important; } footer, .tsqd-open-btn-container { display: none !important; }`}</style>
      {/* The print override is whitened here because this wrapper is the
       * harness's page surround, not a form slot: the theme stylesheet's
       * `@media print` block forces every `--form-*` token to light precision,
       * but it has no reach over an app utility like `dark:bg-gray-900`. Without
       * this, a print screenshot taken from a dark theme showed a correctly
       * light-flipped document floating on a near-black page — the form was right
       * and the baseline read as a failure, which is worse than either being
       * plainly wrong.
       *
       * It needs BOTH the `print:` and the `print:dark:` spelling. Tailwind
       * compiles `dark:` to `:is(.dark, .dark *)`, so `dark:bg-gray-900` carries
       * two selector components while a media query adds no specificity at all —
       * a bare `print:bg-white` loses to it. Same reason the pass/fail pills carry
       * a `print:dark:` triplet. */}
      <div className="min-h-screen bg-gray-50 px-8 py-10 dark:bg-gray-900 print:bg-white print:dark:bg-white">
        <div className="mx-auto max-w-4xl">
          <FormLetterhead
            orgName="Cascade Water Utility"
            programLine="Cross-Connection Control Program"
            logoUrl={null}
            formTitle="Annual Backflow Assembly Test"
            metaLine="visual-fixture · v1 · Entry 1 of 1"
          />
          <FormRenderer
            definition={definition}
            answers={answers}
            onAnswerChange={onAnswerChange}
            language="en"
            theme={theme}
            disabled={state === "disabled" ? true : undefined}
            errorFor={errorFor}
            tagFor={tagFor}
            readContext={READ_CONTEXT}
            attachmentSlot={attachmentSlot}
            facilityRefSlot={renderFacilityRefSlot}
          />
        </div>
      </div>
    </div>
  );
}
